import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { DataGrid } from '@mui/x-data-grid';
import api from '../api';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

function arrayToCSV(data, columns) {
    if (!data || !data.length) return '';
    const header = columns.map(c => c.headerName || c.field);
    const rows = data.map(r => columns.map(c => {
        const v = r[c.field] ?? '';
        const s = String(v).replace(/"/g, '""');
        return s.includes(',') || s.includes('\n') || s.includes('"') ? `"${s}"` : s;
    }).join(','));
    return [header.join(','), ...rows].join('\n');
}

function downloadCSV(csv, filename) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function SchoolContacts() {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggest, setLoadingSuggest] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [error, setError] = useState('');
    const [errorDetails, setErrorDetails] = useState(null);
    const [loadingContacts, setLoadingContacts] = useState(false);

    useEffect(() => {
        if (!inputValue || inputValue.trim().length < 2) {
            setSuggestions([]);
            return;
        }
        let mounted = true;
        const t = setTimeout(async () => {
            setLoadingSuggest(true);
            try {
                const res = await api.get(`/api/public/eligibility/schools?name=${encodeURIComponent(inputValue)}`);
                if (!mounted) return;
                setSuggestions(res.data || []);
            } catch (e) {
                console.error(e);
                if (mounted) setSuggestions([]);
            } finally {
                if (mounted) setLoadingSuggest(false);
            }
        }, 250);
        return () => { mounted = false; clearTimeout(t); };
    }, [inputValue]);

    // (whoami moved to MainLayout header)

    async function loadForSchool(id) {
        setSelectedSchool(null);
        setContacts([]);
        setError('');
        setErrorDetails(null);
        setLoadingContacts(true);
        console.info('SchoolContacts: loading contacts for school id=', id);
        try {
            const aggUrl = `/admin/api/schools/${id}`;
            const invitesUrl = `/api/admin/invite/list?schoolId=${id}`;
            console.debug('SchoolContacts: requesting agg', aggUrl);
            const aggRes = await api.get(aggUrl);
            console.debug('SchoolContacts: agg response', { status: aggRes.status });
            const agg = aggRes.data || {};

            // Try invites, but treat failures as non-fatal; many admin users may not have invite-reading permissions.
            let invites = [];
            try {
                console.debug('SchoolContacts: requesting invites', invitesUrl);
                const invitesRes = await api.get(invitesUrl);
                invites = invitesRes.data || [];
                console.debug('SchoolContacts: invites response', { status: invitesRes.status, count: invites.length });
            } catch (invErr) {
                console.warn('SchoolContacts: invites request failed', invErr?.response?.status, invErr?.message);
                // Surface a friendly non-fatal warning to the UI
                const status = invErr?.response?.status || null;
                setErrorDetails({ status, data: invErr?.response?.data || null, url: invErr?.config?.url || invitesUrl });
                if (status === 403) {
                    setError('Partial data: cannot read invite/collaborator list (403). Attempting fallback to user list.');
                    // Try fallback: fetch /admin/api/users and filter by schoolName
                    try {
                        const usersRes = await api.get('/admin/api/users');
                        console.debug('SchoolContacts: users list fetched', usersRes.status, usersRes.data?.length);
                        const users = usersRes.data || [];
                        const schoolName = agg.schoolName || null;
                        if (schoolName) {
                            const matched = users.filter(u => u.schoolName === schoolName);
                            console.debug('SchoolContacts: matched users by schoolName', matched.length);
                            invites = matched.map(u => ({
                                id: `user-${u.id}`,
                                inviteeFirstName: u.firstName,
                                inviteeLastName: u.lastName,
                                inviteeEmail: u.email,
                                inviteePhone: '',
                                isOwner: (u.role || '').toUpperCase() === 'ADMIN',
                                status: 'ACTIVE'
                            }));
                        } else {
                            console.debug('SchoolContacts: no schoolName available for fallback');
                            invites = [];
                        }
                    } catch (uErr) {
                        console.warn('SchoolContacts: fallback users request failed', uErr?.response?.status, uErr?.message);
                    }
                } else {
                    // For other errors, log and set details but continue
                    setError('Partial data: failed to load invites. Showing available contacts.');
                }
            }

            // Normalize contacts from multiple sources
            // Fetch contacts table (non-fatal)
            let extraContacts = [];
            try {
                const contactsRes = await api.get(`/api/admin/contacts?schoolId=${id}`);
                extraContacts = contactsRes.data || [];
            } catch (cErr) {
                console.warn('SchoolContacts: contacts table fetch failed', cErr?.response?.status);
            }

            // Normalize contacts from multiple sources
            const rows = [];

            // 1. Collaborators: account owner + invited/accepted collaborators (top)
            invites.forEach(i => {
                const name = `${i.inviteeFirstName || ''} ${i.inviteeLastName || ''}`.trim();
                rows.push({
                    id: `invite-${i.id}`,
                    name: name || i.inviteeEmail || '—',
                    title: i.role || '',
                    department: '',
                    email: i.inviteeEmail || '',
                    phone: i.inviteePhone || '',
                    source: i.isOwner ? 'Owner' : (i.status === 'Invited' ? 'Collaborator (Pending)' : 'Collaborator')
                });
            });

            // 2. SchoolProfile contacts
            const sp = agg.schoolProfile || {};
            const pushIf = (label, name, title, dept, email, phone, source) => {
                if (name || title || dept || email || phone) {
                    rows.push({
                        id: `${label}-${rows.length}`,
                        name: name || '—',
                        title: title || '',
                        department: dept || '',
                        email: email || '',
                        phone: phone || '',
                        source
                    });
                }
            };

            pushIf('school-contact', `${sp.contactFirstName || ''} ${sp.contactLastName || ''}`.trim(), sp.contactTitle, sp.contactDepartment, sp.contactEmail, sp.contactPhone, 'SchoolInfo');
            pushIf('president', `${sp.presidentFirstName || ''} ${sp.presidentLastName || ''}`.trim(), 'President', '', sp.presidentEmail, sp.presidentOfficePhone, 'SchoolInfo');
            pushIf('secondary', `${sp.secondaryContactFirstName || ''} ${sp.secondaryContactLastName || ''}`.trim(), sp.secondaryContactTitle, sp.secondaryContactDepartment, sp.secondaryContactEmail, sp.secondaryContactPhone, 'SchoolInfo');

            // 3. ILG contacts
            const ilg = agg.ilgApplication || {};
            pushIf('ilg-primary', `${ilg.ilgQPrimaryCFirstName || ''} ${ilg.ilgQPrimaryCLastName || ''}`.trim(), ilg.ilgQPrimaryCTitle, ilg.ilgQPrimaryCDepartment, ilg.ilgQPrimaryCEmail, ilg.ilgQPrimaryCPhone, 'ILG');
            pushIf('ilg-secondary', `${ilg.ilgQSecondaryCFirstName || ''} ${ilg.ilgQSecondaryCLastName || ''}`.trim(), ilg.ilgQSecondaryCTitle, ilg.ilgQSecondaryCDepartment, ilg.ilgQSecondaryCEmail, ilg.ilgQSecondaryCPhone, 'ILG');
            pushIf('ilg-fac', `${ilg.ilgQFacilitiesCFirstName || ''} ${ilg.ilgQFacilitiesCLastName || ''}`.trim(), ilg.ilgQFacilitiesCTitle, ilg.ilgQFacilitiesCDepartment, ilg.ilgQFacilitiesCEmail, ilg.ilgQFacilitiesCPhone, 'ILG');

            // 4. Contacts table (added via Invite Collaborators page)
            extraContacts.forEach((c, idx) => {
                const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
                if (name || c.email) {
                    rows.push({
                        id: `contact-${c.id || idx}`,
                        name: name || '—',
                        title: c.title || '',
                        department: '',
                        email: c.email || '',
                        phone: c.phone || '',
                        source: 'Contact'
                    });
                }
            });

            // De-dup by email+phone+name
            const seen = new Set();
            const deduped = [];
            for (const r of rows) {
                const key = `${(r.email || '').toLowerCase()}|${(r.phone || '').replace(/[^0-9]/g, '')}|${(r.name || '').toLowerCase()}`;
                if (!seen.has(key)) { seen.add(key); deduped.push(r); }
            }

            setSelectedSchool({ id, schoolName: agg.schoolName || '' });
            setContacts(deduped);
        } catch (e) {
            // Log detailed error for debugging
            console.error('SchoolContacts.loadForSchool error', e, e?.response?.status, e?.response?.data);
            const message = e?.message || 'Unknown error';
            setError(`Failed to load school contacts: ${message}`);
            setErrorDetails({ status: e?.response?.status || null, data: e?.response?.data || null, url: e?.config?.url || null });
        } finally {
            setLoadingContacts(false);
        }
    }

    const columns = useMemo(() => [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
        { field: 'title', headerName: 'Title', flex: 0.8, minWidth: 140 },
        { field: 'department', headerName: 'Department', flex: 0.8, minWidth: 140 },
        {
            field: 'email', headerName: 'Email', flex: 1, minWidth: 200, renderCell: (p) => (
                p.value ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" onClick={() => navigator.clipboard?.writeText(p.value)} title="Copy email"><ContentCopyIcon fontSize="small" /></IconButton>
                        <a href={`mailto:${p.value}`} style={{ color: '#1976d2', textDecoration: 'none' }}>{p.value}</a>
                    </Box>
                ) : '—'
            )
        },
        { field: 'phone', headerName: 'Phone', flex: 0.6, minWidth: 140 },
        { field: 'source', headerName: 'Source', flex: 0.6, minWidth: 120 }
    ], []);

    return (
        <Box sx={{ width: '90%', margin: '36px auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h4" component="h2">School Information</Typography>
                </Box>
                <Box>
                    <Button variant="contained" onClick={() => {
                        const csv = arrayToCSV(contacts, columns.filter(c => c.field !== 'actions'));
                        downloadCSV(csv, `${selectedSchool?.schoolName || 'contacts'}-contacts.csv`);
                    }} disabled={!contacts.length}>Export CSV</Button>
                </Box>
            </Box>

            {error && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity="error">{error}</Alert>
                    {errorDetails && (
                        <Box sx={{ mt: 1, p: 1, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">Error details:</Typography>
                            <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(errorDetails, null, 2)}</pre>
                        </Box>
                    )}
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Autocomplete
                    freeSolo
                    options={suggestions}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : (option.name || ''))}
                    inputValue={inputValue}
                    onInputChange={(e, v, reason) => {
                        setInputValue(v || '');
                    }}
                    onChange={(e, value) => {
                        if (value && typeof value === 'object' && value.id) {
                            setInputValue(value.name || '');
                            setSuggestions([]);
                            loadForSchool(value.id);
                        }
                    }}
                    loading={loadingSuggest}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>{option.name}</Typography>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search school"
                            sx={{ width: 420 }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {inputValue ? (
                                            <IconButton size="small" onClick={() => { setInputValue(''); setSuggestions([]); }}><ClearIcon /></IconButton>
                                        ) : (
                                            <SearchIcon />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </InputAdornment>
                                )
                            }}
                        />
                    )}
                />
            </Box>

            <Box sx={{ mb: 1 }}>
                {selectedSchool && <Typography variant="subtitle1">Showing contacts for: <strong>{selectedSchool.schoolName}</strong> (ID: {selectedSchool.id})</Typography>}
            </Box>

            <Box sx={{ width: '100%' }}>
                <DataGrid
                    rows={contacts.map((r, i) => ({ ...r, id: r.id || i }))}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loadingContacts}
                    disableSelectionOnClick
                    density="comfortable"
                    autoHeight
                />
            </Box>
        </Box>
    );
}
